import { Button, Input, Label } from '@betstarters/ui'
import { useTranslation } from 'react-i18next'
import { ErrorBanner } from './catalog'
import { Checkbox, Combobox, Select } from './controls'
import type { ReferenceItem } from '../hooks/useReference'

/**
 * The create/edit forms of the Finance area, shared by the list dialogs and the
 * detail pages — one definition of the fields, their limits and their hints.
 */

// ---- currency --------------------------------------------------------------

export interface CurrencyFormValue {
  currencyId: string
  currency: string
  currencyCode: string
  currencySymbol: string
  subunit: string
  isEnabled: boolean
  isDisplay: boolean
  isCrypto: boolean
}

export const emptyCurrencyForm: CurrencyFormValue = {
  currencyId: '',
  currency: '',
  currencyCode: '',
  currencySymbol: '',
  subunit: '100',
  isEnabled: true,
  isDisplay: true,
  isCrypto: false,
}

export function isCurrencyFormValid(v: CurrencyFormValue, isCreate: boolean): boolean {
  return (
    v.currency.trim() !== '' &&
    v.currencyCode.trim() !== '' &&
    v.currencySymbol.trim() !== '' &&
    Number(v.subunit) >= 1 &&
    (!isCreate || v.currencyId !== '')
  )
}

export function CurrencyForm({
  value,
  onChange,
  onSubmit,
  onCancel,
  isCreate,
  pending,
  error,
  submitLabel,
}: {
  value: CurrencyFormValue
  onChange: (v: CurrencyFormValue) => void
  onSubmit: () => void
  onCancel: () => void
  /** On create the caller picks the id; on edit it is immutable. */
  isCreate: boolean
  pending: boolean
  error: string
  submitLabel: string
}) {
  const { t } = useTranslation('app')
  const set = (patch: Partial<CurrencyFormValue>) => onChange({ ...value, ...patch })

  return (
    <form
      className="flex max-w-xl flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault()
        if (isCurrencyFormValid(value, isCreate)) onSubmit()
      }}
    >
      <div className="flex gap-3">
        <div className="flex w-28 flex-col gap-1.5">
          <Label htmlFor="currency-id">{t('currencies.columns.id')}</Label>
          <Input
            id="currency-id"
            value={value.currencyId}
            onChange={(e) => set({ currencyId: e.target.value.replace(/[^0-9]/g, '').slice(0, 3) })}
            disabled={!isCreate}
            placeholder="1"
            autoFocus={isCreate}
          />
        </div>
        <div className="flex flex-1 flex-col gap-1.5">
          <Label htmlFor="currency-name">{t('currencies.columns.name')}</Label>
          <Input
            id="currency-name"
            value={value.currency}
            onChange={(e) => set({ currency: e.target.value })}
            maxLength={100}
            placeholder="Euro"
            autoFocus={!isCreate}
          />
        </div>
      </div>
      <p className="-mt-2 text-xs text-muted-foreground">{t('currencies.idHint')}</p>

      <div className="flex gap-3">
        <div className="flex flex-1 flex-col gap-1.5">
          <Label htmlFor="currency-code">{t('currencies.columns.code')}</Label>
          <Input
            id="currency-code"
            value={value.currencyCode}
            onChange={(e) => set({ currencyCode: e.target.value.toUpperCase() })}
            maxLength={10}
            className="font-mono"
            placeholder="EUR"
          />
        </div>
        <div className="flex flex-1 flex-col gap-1.5">
          <Label htmlFor="currency-symbol">{t('currencies.columns.symbol')}</Label>
          <Input
            id="currency-symbol"
            value={value.currencySymbol}
            onChange={(e) => set({ currencySymbol: e.target.value })}
            maxLength={20}
            placeholder="€"
          />
        </div>
        <div className="flex flex-1 flex-col gap-1.5">
          <Label htmlFor="currency-subunit">{t('currencies.columns.subunit')}</Label>
          <Input
            id="currency-subunit"
            value={value.subunit}
            onChange={(e) => set({ subunit: e.target.value.replace(/[^0-9]/g, '').slice(0, 7) })}
            placeholder="100"
          />
        </div>
      </div>
      <p className="-mt-2 text-xs text-muted-foreground">{t('currencies.subunitHint')}</p>

      <div className="flex flex-col gap-2">
        <Checkbox
          id="currency-enabled"
          checked={value.isEnabled}
          onCheckedChange={(checked) => set({ isEnabled: checked })}
          label={t('currencies.columns.enabled')}
        />
        <Checkbox
          id="currency-display"
          checked={value.isDisplay}
          onCheckedChange={(checked) => set({ isDisplay: checked })}
          label={t('currencies.columns.display')}
        />
        <Checkbox
          id="currency-crypto"
          checked={value.isCrypto}
          onCheckedChange={(checked) => set({ isCrypto: checked })}
          label={t('currencies.columns.crypto')}
        />
      </div>
      <p className="-mt-1 text-xs text-muted-foreground">{t('currencies.flagsHint')}</p>

      <ErrorBanner message={error} />

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={pending || !isCurrencyFormValid(value, isCreate)}>
          {submitLabel}
        </Button>
      </div>
    </form>
  )
}

// ---- causal ----------------------------------------------------------------

/** The 25 flag columns, in the order the DDL declares them. */
export const CAUSAL_FLAGS = [
  'isIn',
  'isOut',
  'isManual',
  'isDeposit',
  'isDepositIntoWithdrawableBalance',
  'isWithdrawal',
  'isWithdrawalApproved',
  'isPlayed',
  'isUtilizationBonusBalance',
  'isWin',
  'isRefund',
  'isRollBack',
  'isSession',
  'isAnnulment',
  'isVoid',
  'isEndSession',
  'isWithdrawalOnlyOverdraftBalance',
  'isCashBack',
  'isWithdrawableCash',
  'isBonusRedeemed',
  'isWinTax',
  'isWithdrawalPayable',
  'isDrawer',
  'isPurchased',
  'isFreeSpin',
] as const

export type CausalFlagName = (typeof CAUSAL_FLAGS)[number]
export type CausalFlags = Record<CausalFlagName, boolean>

export const emptyCausalFlags: CausalFlags = Object.fromEntries(
  CAUSAL_FLAGS.map((f) => [f, false]),
) as CausalFlags

export interface CausalFormValue {
  causalId: string
  causal: string
  causalFamilyId: string
  causalGroupId: string
  gameTypeId: string
  transactionTypeId: string
  sign: string
  flags: CausalFlags
}

export const emptyCausalForm: CausalFormValue = {
  causalId: '',
  causal: '',
  causalFamilyId: '',
  causalGroupId: '',
  gameTypeId: '',
  transactionTypeId: '',
  sign: '0',
  flags: emptyCausalFlags,
}

export function isCausalFormValid(v: CausalFormValue, isCreate: boolean): boolean {
  return v.causal.trim() !== '' && v.causalGroupId !== '' && (!isCreate || v.causalId !== '')
}

/** The request body, built once so the list dialog and the detail page agree. */
export function causalPayload(v: CausalFormValue, isDisabledByPlatform: boolean) {
  return {
    causal: v.causal.trim(),
    causalFamilyId: v.causalFamilyId === '' ? null : Number(v.causalFamilyId),
    causalGroupId: Number(v.causalGroupId),
    gameTypeId: v.gameTypeId === '' ? null : Number(v.gameTypeId),
    transactionTypeId: v.transactionTypeId === '' ? null : Number(v.transactionTypeId),
    sign: Number(v.sign),
    flags: v.flags,
    isDisabledByPlatform,
  }
}

const itemsOf = (items: ReferenceItem[]) =>
  items.map((i) => ({ value: String(i.id), label: i.name }))

/**
 * CausalForm edits the identity of a causal: its name, the four things it points
 * at and the sign. The 25 flags are edited separately (CausalFlagsForm) because
 * they are a different question — what the movement means, not what it is.
 */
export function CausalForm({
  value,
  onChange,
  onSubmit,
  onCancel,
  families,
  groups,
  gameTypes,
  transactionTypes,
  isCreate,
  pending,
  error,
  submitLabel,
}: {
  value: CausalFormValue
  onChange: (v: CausalFormValue) => void
  onSubmit: () => void
  onCancel: () => void
  families: ReferenceItem[]
  groups: ReferenceItem[]
  gameTypes: ReferenceItem[]
  transactionTypes: ReferenceItem[]
  isCreate: boolean
  pending: boolean
  error: string
  submitLabel: string
}) {
  const { t } = useTranslation('app')
  const set = (patch: Partial<CausalFormValue>) => onChange({ ...value, ...patch })

  return (
    <form
      className="flex max-w-2xl flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault()
        if (isCausalFormValid(value, isCreate)) onSubmit()
      }}
    >
      <div className="flex gap-3">
        <div className="flex w-28 flex-col gap-1.5">
          <Label htmlFor="causal-id">{t('causals.columns.id')}</Label>
          <Input
            id="causal-id"
            value={value.causalId}
            onChange={(e) => set({ causalId: e.target.value.replace(/[^0-9]/g, '').slice(0, 5) })}
            disabled={!isCreate}
            placeholder="300"
            autoFocus={isCreate}
          />
        </div>
        <div className="flex flex-1 flex-col gap-1.5">
          <Label htmlFor="causal-name">{t('causals.columns.name')}</Label>
          <Input
            id="causal-name"
            value={value.causal}
            onChange={(e) => set({ causal: e.target.value })}
            maxLength={510}
            placeholder="MANUAL.DEPOSIT"
            autoFocus={!isCreate}
          />
        </div>
      </div>
      <p className="-mt-2 text-xs text-muted-foreground">{t('causals.idHint')}</p>

      <div className="flex gap-3">
        <div className="flex flex-1 flex-col gap-1.5">
          <Label htmlFor="causal-group">{t('causals.columns.group')}</Label>
          <Combobox
            value={value.causalGroupId}
            onValueChange={(v) => set({ causalGroupId: v })}
            items={itemsOf(groups)}
            placeholder={t('causals.pickGroup')}
            className="w-full"
          />
        </div>
        <div className="flex flex-1 flex-col gap-1.5">
          <Label htmlFor="causal-family">{t('causals.columns.family')}</Label>
          <Combobox
            value={value.causalFamilyId}
            onValueChange={(v) => set({ causalFamilyId: v })}
            items={itemsOf(families)}
            placeholder={t('causals.noFamily')}
            className="w-full"
          />
        </div>
      </div>

      <div className="flex gap-3">
        <div className="flex flex-1 flex-col gap-1.5">
          <Label htmlFor="causal-gametype">{t('causals.columns.gameType')}</Label>
          <Combobox
            value={value.gameTypeId}
            onValueChange={(v) => set({ gameTypeId: v })}
            items={itemsOf(gameTypes)}
            placeholder={t('causals.noGameType')}
            className="w-full"
          />
        </div>
        <div className="flex flex-1 flex-col gap-1.5">
          <Label htmlFor="causal-transaction">{t('causals.columns.transactionType')}</Label>
          <Combobox
            value={value.transactionTypeId}
            onValueChange={(v) => set({ transactionTypeId: v })}
            items={itemsOf(transactionTypes)}
            placeholder={t('causals.noTransactionType')}
            className="w-full"
          />
        </div>
      </div>

      <div className="flex w-48 flex-col gap-1.5">
        <Label htmlFor="causal-sign">{t('causals.columns.sign')}</Label>
        <Select
          value={value.sign}
          onValueChange={(v) => set({ sign: v })}
          items={[
            { value: '1', label: t('causals.signs.in') },
            { value: '-1', label: t('causals.signs.out') },
            { value: '0', label: t('causals.signs.neutral') },
          ]}
          className="w-full"
        />
      </div>

      <ErrorBanner message={error} />

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={pending || !isCausalFormValid(value, isCreate)}>
          {submitLabel}
        </Button>
      </div>
    </form>
  )
}

/**
 * CausalFlagsForm edits the 25 booleans as one submission rather than switches
 * that fire on click: the flags are read together by the wallet, so a half-saved
 * combination is a wrong combination.
 */
export function CausalFlagsForm({
  value,
  onChange,
  onSubmit,
  onCancel,
  readOnly,
  pending,
  error,
  submitLabel,
}: {
  value: CausalFlags
  onChange: (v: CausalFlags) => void
  onSubmit: () => void
  onCancel: () => void
  readOnly: boolean
  pending: boolean
  error: string
  submitLabel: string
}) {
  const { t } = useTranslation('app')

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault()
        onSubmit()
      }}
    >
      <div className="grid gap-x-8 gap-y-2 sm:grid-cols-2 lg:grid-cols-3">
        {CAUSAL_FLAGS.map((flag) => (
          <Checkbox
            key={flag}
            id={`causal-flag-${flag}`}
            checked={value[flag]}
            disabled={readOnly}
            onCheckedChange={(checked) => onChange({ ...value, [flag]: checked })}
            label={t(`causals.flags.${flag}`)}
          />
        ))}
      </div>

      <ErrorBanner message={error} />

      {!readOnly && (
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={pending}>
            {submitLabel}
          </Button>
        </div>
      )}
    </form>
  )
}
